import { ChevronRight, Loader2, Play, RotateCcw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { moduleIcon } from './eeocUtils';

const STATUS = {
    passed: { label: 'Passed', className: 'bg-emerald-500 text-white hover:bg-emerald-500' },
    due_again: { label: 'Due again', className: 'bg-sky-500 text-white hover:bg-sky-500' },
    attempted: { label: 'Retake needed', className: 'bg-amber-500 text-white hover:bg-amber-500' },
    not_started: { label: 'Not started', className: 'bg-muted text-muted-foreground hover:bg-muted' }
};

const cycleStatus = (module) => {
    if (module.passed_this_cycle) return 'passed';
    return module.status === 'passed' ? 'due_again' : module.status;
};

const actionLabel = (module) => {
    if (module.active_session_id) return { text: 'Resume session', Icon: Play };
    if (module.passed_this_cycle) return { text: 'Practice again', Icon: RotateCcw };
    if (module.status === 'passed') return { text: 'Redo for this period', Icon: RotateCcw };
    if (module.status === 'attempted') return { text: 'Retake module', Icon: RotateCcw };
    return { text: 'Start module', Icon: Play };
};

export const EEOCModuleCard = ({ module, index, onStart, loading, cycleUnit = 'quarter' }) => {
    const Icon = moduleIcon(module.icon);
    const key = cycleStatus(module);
    const status = STATUS[key];
    const label = key === 'passed' ? `Passed this ${cycleUnit}` : key === 'due_again' ? `Due this ${cycleUnit}` : status.label;
    const { text, Icon: ActionIcon } = actionLabel(module);
    return (
        <Card
            className="group relative flex cursor-pointer flex-col overflow-hidden border-sky-200 transition-[box-shadow,transform] duration-200 hover:-translate-y-1 hover:shadow-lg dark:border-sky-800 animate-fade-in"
            style={{ animationDelay: `${index * 50}ms` }}
            onClick={() => !loading && onStart(module)}
            data-testid={`eeoc-module-${module.id}`}
        >
            <div className="h-1.5 bg-gradient-to-r from-sky-500 to-blue-600" />
            <CardContent className="flex flex-1 flex-col gap-3 p-5">
                <div className="flex items-start justify-between gap-2">
                    <div className="rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 p-2.5 text-white shadow transition-transform group-hover:scale-110">
                        <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <Badge className={`text-[10px] ${status.className}`} data-testid={`eeoc-module-status-${module.id}`}>{label}</Badge>
                        <Badge variant="outline" className="text-[10px] font-normal">{module.law}</Badge>
                    </div>
                </div>
                <div>
                    <h3 className="font-semibold leading-snug">{module.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{module.description}</p>
                </div>
                <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
                    <span>{module.scenario_count} voice scenarios</span>
                    {module.best_score !== null && module.best_score !== undefined && (
                        <span data-testid={`eeoc-module-best-${module.id}`}>Best <span className="font-semibold text-foreground">{module.best_score}</span> · {module.attempts} attempt{module.attempts === 1 ? '' : 's'}</span>
                    )}
                </div>
                <div className="flex items-center justify-between border-t border-border/50 pt-3">
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 dark:text-sky-400" data-testid={`eeoc-module-action-${module.id}`}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ActionIcon className="h-4 w-4" />} {text}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-sky-600" />
                </div>
            </CardContent>
        </Card>
    );
};
